import { useMemo, type ReactElement } from 'react'
import { isNotEmpty } from '@terros/common'
import { Link, createFileRoute } from '@tanstack/react-router'
import { Stack } from '@mantine/core'
import { useProfile, usePrograms, useTranslation } from '@front/model'
import { NotReady, Page, TButton } from '@components'
import { ProgramListSection } from './ProgramListSection'
import { AddProgramButton } from './AddProgramButton'

function ProgramsPage(): ReactElement {
  const { t } = useTranslation()
  const { user, isLoading: isProfileLoading, error: profileError } = useProfile()
  const { data: programs, refetch, isFetching, isLoading, error } = usePrograms()

  const { myPrograms, otherPrograms } = useMemo(() => {
    if (!programs || !user?.userId) return { myPrograms: [], otherPrograms: [] }
    const userId = user.userId
    return {
      myPrograms: programs.filter((program) => program.ownerId === userId),
      otherPrograms: programs.filter((program) => program.ownerId !== userId),
    }
  }, [programs, user?.userId])

  if (isLoading || isProfileLoading || error || profileError)
    return (
      <NotReady
        data={programs}
        error={error ?? profileError}
        id='Programs'
        isLoading={isLoading || isProfileLoading}
      />
    )

  return (
    <Page>
      <Page.Heading isFetching={isFetching} refetch={refetch} title={t('Programs')}>
        <AddProgramButton />
        <TButton component={Link} icon='archive' to='trash' tooltip={t('View Trash')} variant='secondary' />
      </Page.Heading>
      <Page.Content scrollArea>
        <Stack gap='xl'>
          <ProgramListSection
            emptyMessage={t('You have not created any programs')}
            programs={myPrograms}
            title={t('My Programs')}
          />
          {isNotEmpty(otherPrograms) && (
            <ProgramListSection programs={otherPrograms} showOwner title={t('Other Programs')} />
          )}
        </Stack>
      </Page.Content>
    </Page>
  )
}

export const Route = createFileRoute('/program/')({
  component: ProgramsPage,
})
